import { type FormEvent, useEffect, useState } from "react";
import {
  type OperatorSummary,
  disableOperator,
  enableOperator,
  getSuperadminApi,
  inviteOperator,
  listOperators,
} from "@praesto/core";
import { Toolbar, Card, Button, Modal, FormField, Input } from "@praesto/ui-kit";

export function OperatorsTab() {
  const [operators, setOperators] = useState<ReadonlyArray<OperatorSummary>>([]);
  const [showInvite, setShowInvite] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const api = getSuperadminApi();
      const tok = await api.getAuthToken();
      const r = await listOperators(api.apiBase, tok);
      if (!cancelled) setOperators(r.operators);
    })().catch((e) => {
      if (!cancelled) setError(String(e));
    });
    return () => {
      cancelled = true;
    };
  }, [reload]);

  async function toggle(op: OperatorSummary) {
    try {
      const api = getSuperadminApi();
      const tok = await api.getAuthToken();
      if (op.status === "active") {
        await disableOperator(api.apiBase, op.id, tok);
      } else {
        await enableOperator(api.apiBase, op.id, tok);
      }
      setReload((r) => r + 1);
    } catch (e) {
      setError(String(e));
    }
  }

  return (
    <div>
      <Toolbar
        title="Operatori"
        actions={
          <Button size="sm" variant="primary" onClick={() => setShowInvite(true)}>
            + Invita operatore
          </Button>
        }
      />
      {error && <p className="text-sm text-danger mb-3">{error}</p>}
      <div className="space-y-2">
        {operators.map((op) => (
          <Card key={op.id} padding="sm">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm text-fg-default truncate">{op.displayName}</div>
                <div className="text-mono-xs text-fg-muted truncate">
                  {op.email} · {op.status}
                </div>
              </div>
              <Button size="sm" variant="ghost" className="text-danger" onClick={() => toggle(op)}>
                {op.status === "active" ? "Disabilita" : "Riattiva"}
              </Button>
            </div>
          </Card>
        ))}
      </div>
      {showInvite && (
        <InviteOperatorModal
          onClose={() => setShowInvite(false)}
          onDone={() => {
            setShowInvite(false);
            setReload((r) => r + 1);
          }}
        />
      )}
    </div>
  );
}

function InviteOperatorModal(props: { onClose: () => void; onDone: () => void }) {
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const api = getSuperadminApi();
      const tok = await api.getAuthToken();
      await inviteOperator(api.apiBase, { email, displayName }, tok);
      props.onDone();
    } catch (e2) {
      setError(String(e2));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open={true} onClose={props.onClose} title="Invita operatore" size="sm">
      <form onSubmit={submit} className="space-y-3">
        <FormField label="Email">
          <Input type="email" value={email} required onChange={(e) => setEmail(e.target.value)} />
        </FormField>
        <FormField label="Nome">
          <Input value={displayName} required onChange={(e) => setDisplayName(e.target.value)} />
        </FormField>
        <div className="flex gap-2 justify-end">
          <Button type="button" size="sm" variant="ghost" onClick={props.onClose}>
            Annulla
          </Button>
          <Button
            type="submit"
            size="sm"
            variant="primary"
            disabled={busy || !email || !displayName}
          >
            {busy ? "…" : "Invita"}
          </Button>
        </div>
        {error && <p className="text-sm text-danger">{error}</p>}
      </form>
    </Modal>
  );
}
